import { CSSProperties, useEffect, useRef } from 'react';

import { RiCloseLine } from '@remixicon/react';
import classNames from 'classnames';

import { ButtonIcon } from '@spotify-internal/encore-web';

import styles from './Lightbox.module.css';

type LightboxProps = {
  src: string;
  alt?: string;
  originRect: DOMRect;
  onClose: () => void;
};

export const Lightbox = ({ src, alt, originRect, onClose }: LightboxProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isClosingRef = useRef(false);

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const horizontalOffset =
    originRect.left + originRect.width / 2 - viewportWidth / 2;
  const verticalOffset =
    originRect.top + originRect.height / 2 - viewportHeight / 2;
  const scale = Math.min(
    originRect.width / (viewportWidth * 0.8),
    originRect.height / (viewportHeight * 0.8),
  );
  const closedTransform = `translate(${horizontalOffset}px, ${verticalOffset}px) scale(${scale})`;

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const handleClose = async () => {
    const dialog = dialogRef.current;
    if (!dialog || isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    dialog.close();

    const transitions = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(
      transitions.map(transition => transition.finished),
    );
    onClose();
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      className={classNames(styles.dialog, 'encore-over-media-set')}
      style={
        { '--lightbox-closed-transform': closedTransform } as CSSProperties
      }
      aria-label={alt ? `${alt} image preview` : 'Image preview'}
      onCancel={event => {
        event.preventDefault();
        void handleClose();
      }}
      onClick={event => {
        if (event.target === event.currentTarget) {
          void handleClose();
        }
      }}
    >
      <ButtonIcon
        variant="tertiary"
        className={styles.closeButton}
        aria-label="Close image preview"
        onClick={() => void handleClose()}
      >
        <RiCloseLine />
      </ButtonIcon>
      <div className={styles.imageContainer}>
        <img src={src} alt={alt ?? ''} className={styles.image} />
      </div>
    </dialog>
  );
};
